import React, { useCallback } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash';

import FLAG from 'config/flag.singleton';
import { useThemeWithBright } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { nowAsIso } from 'utils/date/now.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';

import CN from './Button.module.scss';

const L = logger('Button');

const TIMEOUT = 500;

const Button = React.forwardRef(
  (
    {
      casing,
      children,
      pwId,
      tag,
      tooltip,
      variant,
      variantInvert,
      disabledInvertDark,
      width, // xs | small | medium | auto | fluid | max
      height,
      active,
      selected,
      selectedUniversal,
      disabled,
      stripped,
      onClick,
      xSpace, // add more left-right space around text
      noMargin, // no left-right margin
      noVerticalMargin, // no top-bottom margin
      noPadding, // no padding
      rounded, // rounded border radius
      radius, // custom border radius in px
      type,
      className,

      ...extra
    },
    ref,
  ) => {
    const { isBrightMode, isDarkMode, isLightMode } = useThemeWithBright();

    const handleClick = useCallback(
      (ev) => {
        const now = nowAsIso();

        const isLoggingEnabled = FLAG.loggerSpam || FLAG.loggerButton;
        if (isLoggingEnabled) L.debug('()', 'handleClick()', now, 'tag:', tag, 'ev:', ev);

        const loggedClick = isLoggingEnabled
          ? (ev) => {
              L.debug('()', 'loggedClick()', now, 'tag:', tag, 'ev:', ev);
              return call('Button.onClick', onClick, ev);
            }
          : (ev) => call('Button.onClick', onClick, ev);

        const debouncedClick = debounce(loggedClick, 0, { leading: true, trailing: false, maxWait: TIMEOUT });

        return debouncedClick(ev);
      },
      [onClick, tag],
    );

    return (
      <button
        type={type || 'button'}
        {...extra}
        style={{
          ...(radius ? { borderRadius: `${radius}px` } : {}),
          ...extra.style,
        }}
        data-bem="Button"
        data-tag={fromAnyToTrimmed(tag)}
        data-variant={String(variant)}
        data-variant-invert={String(variantInvert)}
        data-active={String(active)}
        data-disabled={String(disabled)}
        data-selected={String(selected)}
        data-pw-id={fromAnyToTrimmed(pwId)}
        disabled={disabled}
        onClick={handleClick}
        ref={ref}
        className={cn({
          [CN.component]: true,
          [CN[`${casing}Casing`]]: casing, // NOTE: were this casing gone in css?
          [CN[`${variant}Variant`]]: variant,
          [CN[`${width}Width`]]: width,
          [CN[`${height}Height`]]: height,
          [CN.active]: active,
          [CN.selected]: selected, //  The selected styles are for desktop views
          [CN.selectedUniversal]: selectedUniversal, // The selected styles are universal for all views
          [CN.stripped]: stripped,
          [CN.xSpace]: xSpace, // added extra padding around text
          [CN.noMargin]: noMargin,
          [CN.noVerticalMargin]: noVerticalMargin,
          [CN.noPadding]: noPadding,
          [CN.variantInvert]: variantInvert,
          [CN.disabledInvertDark]: disabledInvertDark, // when disabled on dark mode, else lightgray on modals
          [CN.tooltip]: tooltip,
          [CN.lightMode]: isLightMode,
          [CN.darkMode]: isDarkMode,
          [CN.brightMode]: isBrightMode,
          [CN.rounded]: rounded && variant === 'bin',
          [className]: className,
        })}
      >
        {children}
      </button>
    );
  },
);

export default Button;
